<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue"
import { getRecording, getLatestPassedRecording } from "@/services/ProjectService"
import { useRoute } from "vue-router"
import Viewer from "@/common/components/viewer/Viewer.vue"
import type { RecordingDTO } from "@/types/gen";
import { DummyDeviceService, PendingDeviceService } from "@/services/BackendService";

const route = useRoute()

const recordingId = ref(parseInt(route.params.recordingId as string))
const testcaseId = ref(parseInt(route.params.testcaseId as string))
const initialFrame = ref(route.query.n || route.query.start || 0)
const autoplay = ref(route.query.autoplay === "true")
const end = ref(route.query.end || 0)
const navigation = ref(route.query.navigation === "false")
const recording = ref<RecordingDTO>()
const device = ref(new PendingDeviceService())
const consoleExpanded = ref(false)
const currentFrameIndex = ref(0)

const consoleOutput = computed<string | null>(() => {
    const out = (recording.value?.meta as any)?.consoleOutput
    return typeof out === "string" && out.length ? out : null
})

// Per-action console captured for the currently-displayed frame.
// Independent of the recording-level aggregate above.
const frameConsoleOutput = computed<string | null>(() => {
    const frames: any[] = recording.value?.frames as any[] || []
    const f = frames[currentFrameIndex.value]
    const out = f?.meta?.consoleOutput
    return typeof out === "string" && out.length ? out : null
})

onMounted(resetRecording)

watch(
    () => route.query.n,
    (n) => {
        initialFrame.value = parseInt(n as string) || 0
    }
)

async function resetRecording() {
    if (recordingId.value) {
        recording.value = await getRecording(recordingId.value)
    } else {
        recording.value = await getLatestPassedRecording(testcaseId.value)
    }
    device.value = new DummyDeviceService(recording.value)
    recording.value.frames = recording.value.frames.filter((frame: any) => frame.imageUrl)
}
</script>

<template lang="pug">
.recording-content
    viewer.viewer-component-wrapper.viewer-pane(
        v-if="recording != null",
        :recording="recording",
        :device="device"
        :initialFrame="initialFrame",
        :autoplay="autoplay",
        :navigation="navigation",
        :lastFrame="end",
        @currentFrame="(i) => currentFrameIndex = i"
    )
    .console-pane.hide-in-iframe(v-if="recording != null")
        .console-header(@click="consoleExpanded = !consoleExpanded")
            i.fas(:class="consoleExpanded ? 'fa-caret-down' : 'fa-caret-right'")
            span.label Console output
            span.count(v-if="consoleOutput") ({{ consoleOutput.split('\n').length }} aggregate · {{ frameConsoleOutput ? frameConsoleOutput.split('\n').length : 0 }} this frame)
            span.count.empty(v-else) (none captured)
        .console-bodies(v-if="consoleExpanded")
            div(v-if="frameConsoleOutput")
                .sub-label This frame:
                pre.console-body {{ frameConsoleOutput }}
            div(v-if="consoleOutput")
                .sub-label Aggregate (last 50 lines):
                pre.console-body {{ consoleOutput }}
            .console-body.console-empty(v-if="!consoleOutput && !frameConsoleOutput")
                | No console output captured. Enable "Capture Web Console" in Test Settings → Settings.
</template>

<style lang="css" scoped>
.recording-content {
    height: 100vh;
    display: flex;
    flex-direction: column;
}
.recording-content :v-deep(.hide-in-iframe) {
    display: none;
}
.viewer-pane {
    flex: 1 1 auto;
    min-height: 0;
}
.console-pane {
    flex: 0 0 auto;
    border-top: 1px solid #ddd;
    background: #fafafa;
    font-size: 12px;
}
.console-header {
    cursor: pointer;
    padding: 6px 12px;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 6px;
    background: #f0f0f0;
}
.console-header:hover {
    background: #e8e8e8;
}
.console-header .label {
    font-weight: 600;
}
.console-header .count {
    color: #666;
}
.console-header .count.empty {
    color: #999;
    font-style: italic;
}
.console-bodies {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.sub-label {
    font-size: 11px;
    color: #888;
    padding: 4px 12px 0;
}
.console-body {
    margin: 0;
    padding: 8px 12px;
    max-height: 200px;
    overflow: auto;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    white-space: pre-wrap;
    background: #1e1e1e;
    color: #ddd;
}
.console-body.console-empty {
    color: #888;
    background: #fafafa;
    font-family: inherit;
    font-style: italic;
}
</style>
